import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { tokenStore } from '../../auth/tokenStore';
import { authEvents } from '../../auth/authEvents';
import { newCorrelationId } from './correlationId';

/*
 * Axios client — CRS-Mobile-Developer-Guide.md §6.1.
 * Talks ONLY to crs-mobile-api. Never sends X-Tenant-ID / X-App-ID / role headers —
 * tenant context is derived by the BFF from the mobile session.
 */

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Silent refresh via TenantHub. Called by the response interceptor when a
 * request comes back 401. Bypasses the axios `instance` on purpose so the
 * refresh call itself can't loop through the interceptor.
 *
 * Wire path: BFF `/auth/refresh-hub` proxies to TenantHub's
 * `/api/v1/Auth/refresh`. Uses `X-Internal-Api-Key` to authenticate the BFF
 * to TenantHub; TenantHub validates the refresh token and mints a new
 * Keycloak-signed access token. The response envelope is `{ success, data: {
 * accessToken, refreshToken, ... } }` — we tolerate both nested and flat
 * shapes for compatibility with older proxy versions.
 *
 * NOTE: the OLD implementation hit BFF `/auth/refresh` which was (a) gated
 * by `[Authorize]` (chicken-and-egg with an expired token) and (b) issued a
 * local HMAC-signed token that the BFF's Keycloak-configured JWT bearer
 * would then reject. That's why refresh silently failed and every API
 * returned 401 after the first hour. Fixed 2026-07-20.
 */
async function singleFlightRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const refreshToken = tokenStore.getRefreshToken();
      if (!refreshToken) return null;

      // Tenant + app IDs live-look from tokenStore — TenantHub needs them
      // to key the refresh token to the right realm/client.
      const tenantId = tokenStore.getTenantId();
      const applicationId = tokenStore.getAppId();

      const headers: Record<string, string> = {
        'X-Correlation-ID': newCorrelationId(),
      };
      // Even though the refresh endpoint is anonymous, include X-Tenant-ID
      // + X-App-ID so any tenant-header validation middleware doesn't 400
      // before the body is read.
      if (tenantId) headers['X-Tenant-ID'] = tenantId;
      if (applicationId) headers['X-App-ID'] = applicationId;

      const res = await axios.post<unknown>(
        `${import.meta.env.VITE_MOBILE_API_BASE_URL}/auth/refresh-hub`,
        {
          refreshToken,
          tenantId: tenantId ?? undefined,
          applicationId: applicationId ?? undefined,
        },
        { headers },
      );

      // TenantHub responds with `{ success, data: { accessToken, refreshToken, ... } }`.
      // Fall back to a flat shape for backwards-compat with older proxies.
      const body = res.data as { success?: boolean; data?: unknown } & Record<string, unknown>;
      const tokenData = (body.data ?? body) as {
        accessToken?: string;
        token?: string;
        refreshToken?: string;
      };
      const newAccess = tokenData.accessToken ?? tokenData.token;
      const newRefresh = tokenData.refreshToken;

      if (!newAccess) return null;

      tokenStore.setAccessToken(newAccess);
      if (newRefresh) tokenStore.setRefreshToken(newRefresh);
      return newAccess;
    } catch {
      // Refresh failed — treat as logged out. SessionProvider listens for
      // the 'cleared' event and drops session state so ProtectedGate
      // redirects to /login on the next render.
      tokenStore.clear();
      authEvents.emit('cleared');
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

export function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_MOBILE_API_BASE_URL,
    timeout: 15_000,
  });

  instance.interceptors.request.use((config) => {
    const at = tokenStore.getAccessToken();
    if (at) config.headers.set('Authorization', `Bearer ${at}`);

    const entityId = tokenStore.getSelectedPropertyId();
    if (entityId) config.headers.set('X-Entity-ID', entityId);

    // X-Tenant-ID — dynamic. Set at login from the TenantHub authenticate
    // response (authService.login → tokenStore.setTenantId). Pre-login falls
    // back to DEV_DEFAULTS.tenantId so /auth/discover can proxy correctly.
    //
    // X-App-ID — pinned. Sourced from VITE_APP_ID (per-env Keycloak client ID)
    // via DEV_DEFAULTS.appId. Never overwritten by the authenticate response
    // (see comment in authService.login) so every request in the session
    // carries the same client ID that /auth/discover was resolved against.
    const tenantId = tokenStore.getTenantId();
    if (tenantId) config.headers.set('X-Tenant-ID', tenantId);

    const appId = tokenStore.getAppId();
    if (appId) config.headers.set('X-App-ID', appId);

    if (!config.headers.get('X-Correlation-ID')) {
      config.headers.set('X-Correlation-ID', newCorrelationId());
    }
    config.headers.set('X-Timezone', browserTimezone());
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const url = original?.url ?? '';
      const isAuthEndpoint = url.startsWith('/auth/');

      if (
        error.response?.status === 401 &&
        original &&
        !original._retry &&
        !isAuthEndpoint
      ) {
        original._retry = true;
        const newToken = await singleFlightRefresh();
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return instance.request(original);
        }
        // singleFlightRefresh has already cleared tokens + emitted 'cleared';
        // SessionProvider will drop the session and ProtectedGate will
        // redirect to /login on the next render. Just reject.
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

export const apiClient = createApiClient();
